import type { Article, Topic } from "@/types/melon";

export const topics: Topic[] = [
  {
    slug: "yule-shijian",
    name: "娱乐事件",
    description: "行程、同框、声明与后续回应，一条条按时间戳排清楚。",
    icon: "ri-sparkling-2-line",
    tone: "primary",
  },
  {
    slug: "yingshi-xuanfa",
    name: "影视宣发",
    description: "戏份、物料、排播与番位，宣传期里的每一次改动都有痕迹。",
    icon: "ri-clapperboard-line",
    tone: "accent",
  },
  {
    slug: "zongyi-muhou",
    name: "综艺幕后",
    description: "剪辑、歌单、赛制与现场观众记录，还原录制现场的原始顺序。",
    icon: "ri-mic-line",
    tone: "secondary",
  },
  {
    slug: "daiyan-fengbo",
    name: "品牌代言",
    description: "官宣、下架、解约与替换，物料上下线的分钟级记录。",
    icon: "ri-store-3-line",
    tone: "primary",
  },
  {
    slug: "yinyue-xianchang",
    name: "音乐现场",
    description: "巡演、改期、票务与退改，现场之外的连锁反应同样值得复盘。",
    icon: "ri-music-2-line",
    tone: "secondary",
  },
  {
    slug: "wangluo-redian",
    name: "网络热点",
    description: "热榜、二创与截图考据，看一条内容如何被反复改写。",
    icon: "ri-fire-line",
    tone: "accent",
  },
];

export const articles: Article[] = [
  {
    id: 1,
    slug: "music-show-song-swap-72hours",
    title: "某音乐竞演节目临时换歌：从彩排到直播的 72 小时完整时间线",
    excerpt:
      "彩排曲目在直播前约 12 小时被替换，歌单、字幕文件与乐队谱面先后改动。我们把 72 小时里公开出现的每一条痕迹排成一条时间线，并标注了哪些环节至今仍然没有解释。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20empty%20concert%20stage%20with%20a%20single%20warm%20spotlight%20and%20dust%20in%20the%20air%2C%20muted%20terracotta%20and%20cream%20tones%2C%20abstract%20geometric%20shapes%2C%20grainy%20paper%20texture%2C%20minimal%20magazine%20cover%20aesthetic%2C%20no%20people&width=1200&height=750&seq=melon-cover-01&orientation=landscape",
    coverAlt: "舞台聚光灯下的空舞台插画",
    topicSlug: "zongyi-muhou",
    topicName: "综艺幕后",
    date: "2026-08-21",
    readTime: "9 分钟",
    author: "陆迟",
    authorRole: "时间线编辑",
    heat: 96,
    status: "未删减原稿已核",
    tags: ["换歌风波", "节目录制", "时间线"],
    highlights: [
      "曲目变更通知发出于首次彩排结束后的深夜，理由栏只写了四个字：版权确认。",
      "后期团队在直播前 4 小时收到第二版歌词文件，第一版被直接标记为作废。",
      "现场乐队拿到最终谱面时，距离开播只剩 40 分钟。",
    ],
    sections: [
      {
        heading: "一、换歌这件事，最先被谁看见",
        paragraphs: [
          "最早的可核对痕迹来自现场观众拍下的歌单照片。第一轮彩排结束时，歌单上印的还是原定曲目。照片拍摄时间与场馆入场记录能够互相印证，因此可以确认：变更不是一开始就安排好的。",
          "真正让讨论扩散的，是第二轮联排时流出的十几秒视频片段。同一段旋律，前后两天的调性出现了明显下移。对熟悉现场的人来说，这比任何一句声明都更直接。",
        ],
      },
      {
        heading: "二、被改动的其实不止一首歌",
        paragraphs: [
          "把前后两版歌单叠在一起看会发现，替换曲目的位置恰好卡在整场演出的中段。这个位置承担着情绪转折功能，临时更换会牵动灯光、走位与串场词，成本远高于换掉一首开场的歌。",
          "更关键的是字幕。第二版歌词文件在直播前 4 小时才送达后期，这意味着字幕组几乎没有时间做逐句校对。直播画面里出现的两处错字，恰好都落在新歌的第二段主歌，这是一条可以验证的旁证。",
        ],
      },
      {
        heading: "三、除了版权，还有别的解释吗",
        paragraphs: [
          "把「版权确认」当成唯一解释并不充分。版权谈判通常有前置流程，极少在彩排结束后的深夜才落地；而从通知到直播只有不到两天，这个节奏更像是一次临时起意的规避动作。",
          "另一种更谨慎的读法是：变更并非针对某位歌手，而是为了整体赛制的均衡。无论哪一种，节目组至今没有给出更细的说明，这也正是它至今仍被反复讨论的原因。",
        ],
      },
    ],
    timeline: [
      { at: "周四 21:10", title: "首轮彩排结束", detail: "按原定曲目完成，现场观众拍到的歌单为原曲目版本。" },
      { at: "周四 23:40", title: "曲目变更通知", detail: "节目组向艺人团队发出变更通知，理由一栏写着「版权确认中」。" },
      { at: "周五 09:20", title: "编曲重新制作", detail: "编曲团队收到新版 demo，主歌保留，副歌整体下移一个小三度。" },
      { at: "周五 14:00", title: "字幕文件替换", detail: "后期收到第二版歌词文件，第一版被标记为作废。" },
      { at: "周六 10:30", title: "乐队拿到最终谱面", detail: "距离开播还有 40 分钟，现场合排仅进行了两遍。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 导演组工作群片段",
      lines: [
        "「先按 B 方案走，灯光不用大改，串场词我这边出第二版。」",
        "「谱面几点能给？乐队那边要看总谱，不是分谱。」",
        "「字幕先别删，等确认函，万一又换回来。」",
      ],
    },
    openQuestions: [
      "变更通知里的「版权确认中」，最终由谁出具了确认结果？",
      "第二版歌词文件的校对流程被压缩到了多久？",
      "灯光与走位的调整是否意味着变更并非临时起意？",
    ],
    relatedSlugs: ["variety-cut-uncut-draft", "endorsement-termination-48hours"],
  },
  {
    id: 2,
    slug: "drama-screen-time-count",
    title: "热播剧男二号戏份骤减？我们把它出现过的每一集都数了一遍",
    excerpt:
      "从第十集开始，这个角色的单人镜头明显变少，但台词总量并没有同步下降。把逐集数据排开之后，我们找到了一处容易被忽略的结构性改动。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20stacked%20film%20script%20pages%20and%20a%20film%20clapperboard%20on%20a%20warm%20cream%20desk%2C%20terracotta%20and%20amber%20color%20blocking%2C%20abstract%20shapes%2C%20paper%20grain%20texture%2C%20no%20people&width=1200&height=750&seq=melon-cover-02&orientation=landscape",
    coverAlt: "剧本分镜与场记板插画",
    topicSlug: "yingshi-xuanfa",
    topicName: "影视宣发",
    date: "2026-08-14",
    readTime: "11 分钟",
    author: "顾一鸣",
    authorRole: "事实核查",
    heat: 92,
    status: "逐集数据已回看",
    tags: ["戏份统计", "逐集数据", "结构改动"],
    highlights: [
      "角色单人镜头从第十集起平均每集减少约 1 分 40 秒。",
      "但台词字数的下降幅度只有镜头时长的三分之一，说明删的是画面不是内容。",
      "有两集出现了明显的「群体镜头替代单人镜头」现象。",
    ],
    sections: [
      {
        heading: "一、怎么数才算公平",
        paragraphs: [
          "统计戏份最容易出错的地方，是把群像镜头整体算给某一个角色。我们的做法是只统计「画面中只出现该角色或以其为视觉中心」的镜头，并在出现分歧的镜头下标注了时间码，方便读者自行回看。",
          "另一个变量是每集总时长。同样的一份戏份，放在 42 分钟和 46 分钟的集数里，占比完全不同，所以最终结果以比例呈现。",
        ],
      },
      {
        heading: "二、下降发生在剪辑，而不是拍摄",
        paragraphs: [
          "如果把台词字数与镜头时长分别画成两条线，会看到一个反常识的结果：台词几乎没少，镜头却明显减少。这通常意味着素材本身是充足的，减少发生在后期取舍阶段。",
          "第十一集里有一个很典型的位置：角色独自坐在楼梯间的一段独白，最终被处理成了背影加画外音。信息保留了，画面上的存在感却被压低了。",
        ],
      },
      {
        heading: "三、被替换成了什么",
        paragraphs: [
          "减少的镜头并没有凭空消失。在第十二集与第十四集，同一时间轴上出现了更长的群像场景，交代的是主线案件线索。这说明取舍的优先顺序被调整过，而调整的方向与主线推进的需求一致。",
          "这未必指向任何私人原因。对于一部中等体量的剧集，中后段压缩支线是常规操作。真正值得追问的，是为什么这件事在宣传期完全没有被提及。",
        ],
      },
    ],
    timeline: [
      { at: "开播前 6 天", title: "首支预告发布", detail: "预告中该角色的单人镜头占比接近 20%。" },
      { at: "第 1 集", title: "正片上线", detail: "角色单人镜头占比 14.2%，与预告基本吻合。" },
      { at: "第 10 集", title: "出现明显转折", detail: "单人镜头占比降至 7.8%，是开播以来最低值。" },
      { at: "第 12 集", title: "群像镜头替代", detail: "增加约 2 分 10 秒的群像线索戏，同期单人镜头减少。" },
      { at: "第 16 集", title: "占比略有回升", detail: "回升到 10.4%，但已不再回到前期水平。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 场记单摘录（节选）",
      lines: [
        "「楼梯间独白，全景一条，近景两条，导演要背面收。」",
        "「这条先留着，看后面需不需要画外音。」",
        "「群像那场加，人物别都堆在一个景别里。」",
      ],
    },
    openQuestions: [
      "第十集的调整是否与播出平台的时长要求有关？",
      "预告片镜头与正片的差异，是在哪一版剪辑时产生的？",
      "被替换的单人镜头素材是否仍存在于成片之外的版本中？",
    ],
    relatedSlugs: ["music-show-song-swap-72hours", "interview-90-seconds"],
  },
  {
    id: 3,
    slug: "endorsement-termination-48hours",
    title: "代言在 48 小时内被撤下：一条物料删除记录还原的完整经过",
    excerpt:
      "官网专题页、户外大屏与电商主图并非同时下线。三个渠道的删除时间差接近 30 小时，这个时间差恰好说明了决策是怎么传导的。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20empty%20advertising%20light%20box%20on%20a%20brick%20wall%20at%20dusk%2C%20warm%20rust%20and%20cream%20palette%2C%20abstract%20geometric%20composition%2C%20paper%20grain%20texture%2C%20calm%20minimal%20magazine%20style%2C%20no%20people&width=1200&height=750&seq=melon-cover-03&orientation=landscape",
    coverAlt: "夜幕下的广告灯箱插画",
    topicSlug: "daiyan-fengbo",
    topicName: "品牌代言",
    date: "2026-08-09",
    readTime: "8 分钟",
    author: "林知",
    authorRole: "主编",
    heat: 89,
    status: "渠道记录已核",
    tags: ["代言解约", "物料下架", "时间差"],
    highlights: [
      "品牌官网专题页最早下线，比户外大屏提前约 30 小时。",
      "电商主图的替换发生在第三个批次，恰好对应工作日的第一天。",
      "三个渠道的物料版本号并不一致，说明存在多套并行素材。",
    ],
    sections: [
      {
        heading: "一、三条渠道，三种节奏",
        paragraphs: [
          "赞助与代言物料的撤下通常不会同步。官网可以随时改，户外大屏需要走投放方流程，电商主图则要配合平台审核。把它们放在同一时间轴上，本质上是在观察一次决策的传导路径。",
          "本次记录的顺序是：官网专题页先消失，随后是户外大屏，最后是电商渠道。这个顺序与流程复杂程度完全吻合，说明不存在「紧急全域下线」的动作。",
        ],
      },
      {
        heading: "二、为什么说这不是一次危机公关",
        paragraphs: [
          "如果是突发事件触发的紧急处理，通常会先撤曝光量最大的渠道，再回头处理官网。而这次恰恰相反，最早动的就是维护成本最低的官网。",
          "更值得注意的细节是版本号。三个渠道使用的主视觉版本并不相同，其中电商渠道使用的还是上一季度素材版的构图，这也解释了为什么它更换得最晚。",
        ],
      },
      {
        heading: "三、留下的判断题",
        paragraphs: [
          "合约到期与提前终止在动作上是相似的，区别只在于公告怎么写。品牌方没有发布任何解约声明，这本身也是一种表态。",
          "在没有正式声明的阶段，最稳妥的读法是把它当成一次正常的素材迭代，同时保留后续出现新信息的可能。",
        ],
      },
    ],
    timeline: [
      { at: "周一 10:05", title: "官网专题页下线", detail: "原合作专题页返回 404，导航入口被移除。" },
      { at: "周一 18:30", title: "社交媒体停止投放", detail: "品牌方账号的合作内容被移出首页合集。" },
      { at: "周二 20:12", title: "户外大屏内容替换", detail: "距官网下线约 34 小时，投放方完成画面更换。" },
      { at: "周三 09:40", title: "电商主图更新", detail: "商品详情页使用新一季素材，旧版主视觉不再出现。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 素材交接单片段",
      lines: [
        "「官网先撤，别等大屏，那边走流程慢。」",
        "「电商主图用 24 版素材，不用现在这版。」",
        "「不发声明，按到期处理。」",
      ],
    },
    openQuestions: [
      "合约的原定到期日是哪一天？",
      "电商渠道使用的旧版素材是否影响了下线节奏？",
      "是否存在一份未被公开的补充协议？",
    ],
    relatedSlugs: ["music-show-song-swap-72hours", "airport-photo-timeline"],
  },
  {
    id: 4,
    slug: "variety-cut-uncut-draft",
    title: "综艺剪辑争议里的未删减原稿：镜头之外的 6 个细节",
    excerpt:
      "同一段对话在正片里被拆成了三个位置。把原始顺剪稿的标记叠上去后，我们看到的不只是一次剪辑，而是六次有意为之的顺序调整。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20editing%20timeline%20with%20colorful%20clips%20on%20a%20warm%20cream%20background%2C%20abstract%20blocky%20shapes%20in%20forest%20green%20and%20amber%2C%20paper%20grain%20texture%2C%20minimal%20magazine%20cover%20aesthetic%2C%20no%20people&width=1200&height=750&seq=melon-cover-04&orientation=landscape",
    coverAlt: "剪辑时间线的抽象插画",
    topicSlug: "zongyi-muhou",
    topicName: "综艺幕后",
    date: "2026-07-30",
    readTime: "10 分钟",
    author: "周不晚",
    authorRole: "视觉编辑",
    heat: 87,
    status: "顺剪稿已对照",
    tags: ["剪辑争议", "顺剪稿", "镜头顺序"],
    highlights: [
      "正片中一段 4 分钟的对话，实际来自顺剪稿里三个不相邻的片段。",
      "有 6 处反应镜头的插入改变了原本的语气，其中 4 处增强了冲突感。",
      "被删掉的一句缓冲用词，恰好落在争议最集中的位置。",
    ],
    sections: [
      {
        heading: "一、顺剪稿和成片的距离",
        paragraphs: [
          "顺剪稿是把当天素材按时间顺序粗排的版本，它不代表最终立场，但能反映现场真实发生的顺序。本次对照中，两者的差异集中在反应镜头的插入位置。",
          "反应镜头本身没有错。问题是当它被放在一句话的中间时，同一句话的语气就变了。这也是大多数剪辑争议真正的技术来源。",
        ],
      },
      {
        heading: "二、六处调整分别改变了什么",
        paragraphs: [
          "四处调整把原本平缓的对话推向了更明显的对立，另外两处则是为了压缩时长而做的合并。后者是常规操作，前者才是讨论的焦点。",
          "有意思的是，这句缓冲用词并不长，删掉它只节省了不到两秒。也就是说，如果只是为了控时长，完全没必要动它。",
        ],
      },
      {
        heading: "三、剪辑是表达，不是造假",
        paragraphs: [
          "把所有差异都解释成「恶意剪辑」并不公平。综艺需要在有限时长里讲故事，顺序调整是行业惯例。真正值得要求的，是当争议发生时，平台能公布原始顺序而不是继续沉默。",
          "观众其实并不排斥被剪辑，排斥的是被剪辑之后还被要求相信那是全部。",
        ],
      },
    ],
    timeline: [
      { at: "录制当天 16:20", title: "原始对话发生", detail: "顺剪稿中，这段对话连续且平缓，无冲突性表述。" },
      { at: "录制当天 16:28", title: "缓冲用词出现", detail: "一句用来缓和气氛的表述，时长约 1.8 秒。" },
      { at: "剪辑第 2 轮", title: "反应镜头插入", detail: "在第 3 句与第 4 句之间插入两处反应镜头。" },
      { at: "剪辑第 4 轮", title: "对话被拆分", detail: "整段对话被拆到正片三个不同位置。" },
      { at: "播出后 8 小时", title: "讨论集中爆发", detail: "争议集中在被删掉缓冲用词的那一处。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 顺剪稿台词标注",
      lines: [
        "「先别急，我还没说完。」（该句未进入正片）",
        "「这段留着，情绪够。」",
        "「反应镜头再收一个，放在第二句后面。」",
      ],
    },
    openQuestions: [
      "顺剪稿是否还有更早的版本？",
      "被删掉的缓冲用词，是导演组还是后期决定的？",
      "平台是否会在争议后公布完整录制顺序？",
    ],
    relatedSlugs: ["music-show-song-swap-72hours", "interview-90-seconds"],
  },
  {
    id: 5,
    slug: "concert-reschedule-chain",
    title: "演唱会临时改期的连锁反应：票务、机酒与退改的三方时间线",
    excerpt:
      "改期公告只有一行字，但它的影响会沿着票务系统、出行订单和退改规则一路传导。我们把三方的时间线叠在一起，顺序比想象中更清楚。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20empty%20stadium%20seat%20row%20at%20sunset%20with%20a%20paper%20ticket%20resting%20on%20one%20seat%2C%20warm%20amber%20and%20rust%20palette%2C%20abstract%20composition%2C%20paper%20grain%20texture%2C%20no%20people&width=1200&height=750&seq=melon-cover-05&orientation=landscape",
    coverAlt: "夕阳下空座席与门票的插画",
    topicSlug: "yinyue-xianchang",
    topicName: "音乐现场",
    date: "2026-07-18",
    readTime: "7 分钟",
    author: "陆迟",
    authorRole: "时间线编辑",
    heat: 84,
    status: "退改规则已核",
    tags: ["演唱会改期", "退改规则", "连锁反应"],
    highlights: [
      "公告发出时，票务系统的退票入口尚未开放，中间空了约 5 小时。",
      "出行订单的改期窗口普遍短于演出改期周期，这是损失的主要来源。",
      "绝大多数退改费用争议，都源自这三个时间窗口没有对齐。",
    ],
    sections: [
      {
        heading: "一、公告发出之后，系统还没跟上",
        paragraphs: [
          "改期公告通常由主办方发布，但退票入口由票务平台控制，两者之间总会存在时间差。本次记录到的间隔约 5 小时，这段时间恰好是咨询量最集中的时段。",
          "对这个区间里的观众来说，最难的不是结果，而是不知道规则是什么。",
        ],
      },
      {
        heading: "二、真正产生损失的是第三个环节",
        paragraphs: [
          "演出改期本身通常不产生费用，票可以退也可以留。真正让人被动的是已经预订的出行订单，它们的免费改期窗口往往只有 24 到 48 小时。",
          "当演出改期给出的缓冲期长于航司的免费改期窗口时，时间差就会转化成实际支出。这不是任何一方故意设计的，而是三套规则各自为政的结果。",
        ],
      },
      {
        heading: "三、可以做但没做的事",
        paragraphs: [
          "主办方无法替票务平台和航司做决定，但可以提前同步可能的调整窗口。多数情况下，主办方内部早已知道风险，只是没有把信息提前给到观众。",
          "这也是这类事件反复发生却不被真正解决的原因：每一方都在自己的规则内合规，合起来却让观众承担了全部不确定性。",
        ],
      },
    ],
    timeline: [
      { at: "D-9 天 14:00", title: "主办方内部确认风险", detail: "场地档期存在冲突，内部开始评估改期方案。" },
      { at: "D-6 天 20:30", title: "改期公告发布", detail: "公告仅说明新日期，未同步说明退改细则。" },
      { at: "D-6 天 20:35", title: "退票入口未开放", detail: "票务平台的退票通道在约 5 小时后才上线。" },
      { at: "D-4 天 09:00", title: "出行免费改期窗口关闭", detail: "多数机酒订单的最晚免费调整时间在此前后到期。" },
      { at: "D-2 天 11:20", title: "补充说明发布", detail: "平台公布退票流程与手续费说明。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 观众来信摘录",
      lines: [
        "「票可以退，但我提前订的机票改期费比票还贵。」",
        "「公告里没写退票入口什么时候开，我只能一直刷。」",
      ],
    },
    openQuestions: [
      "改期风险在公告发布前是否已完全确认？",
      "票务平台为何没有在公告同时开放退票入口？",
      "主办方是否会承担跨平台的改期差价？",
    ],
    relatedSlugs: ["music-show-song-swap-72hours", "variety-cut-uncut-draft"],
  },
  {
    id: 6,
    slug: "interview-90-seconds",
    title: "那段被剪掉的 90 秒访谈：完整对话稿与前后文对照",
    excerpt:
      "流传最广的那句回答，前面其实还有一段铺垫。把完整对话稿按顺序排好后会发现，脱离语境的并不是那一句，而是它前面被删掉的两句。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20pair%20of%20empty%20microphones%20on%20a%20table%20with%20warm%20window%20light%2C%20cream%20and%20forest%20green%20palette%2C%20abstract%20shapes%2C%20paper%20grain%20texture%2C%20calm%20minimal%20magazine%20style%2C%20no%20people&width=1200&height=750&seq=melon-cover-06&orientation=landscape",
    coverAlt: "桌面上话筒的插画",
    topicSlug: "wangluo-redian",
    topicName: "网络热点",
    date: "2026-07-05",
    readTime: "6 分钟",
    author: "顾一鸣",
    authorRole: "事实核查",
    heat: 81,
    status: "完整对话稿已对照",
    tags: ["访谈片段", "语境", "对话稿"],
    highlights: [
      "被广泛传播的那句回答，前面原本还有两句限定条件的铺垫。",
      "剪辑保留了结论，删掉了前提，这是语境变化最常见的形态。",
      "完整对话稿中的措辞比流传版本缓和得多。",
    ],
    sections: [
      {
        heading: "一、流传版本少了什么",
        paragraphs: [
          "在多个平台的二次传播中，这段访谈被压缩到了不到十秒。压缩的过程中，两句说明前提的话被优先删除，因为它们让结论显得不够有冲击力。",
          "这不是某一次传播的失误，而是一种结构性的结果：所有需要快速传播的内容，都会优先保留最锋利的部分。",
        ],
      },
      {
        heading: "二、把前提加回来之后",
        paragraphs: [
          "补回前提之后，同一句话的性质发生了变化，从一句绝对判断变成了一种有条件的个人看法。这正是完整对话稿存在的意义。",
          "我们做的对照并不复杂，只是把时间轴还原，然后逐句确认哪里被删、删了多少秒。",
        ],
      },
      {
        heading: "三、作为读者可以做的一件事",
        paragraphs: [
          "面对任何一段剪辑片段，先问一句：前面那两句去哪了。多数时候，答案就藏在这一问里。",
          "这也是深瓜社一直强调的：不急着下结论，先把时间线理清楚。",
        ],
      },
    ],
    timeline: [
      { at: "访谈 00:00", title: "提问背景铺垫", detail: "主持人先交代了讨论的前提与限定条件。" },
      { at: "访谈 01:12", title: "被删掉的前两句", detail: "受访者说明了适用范围的限定条件，共约 18 秒。" },
      { at: "访谈 01:30", title: "流传最广的那句回答", detail: "结论性表述出现在此处，随后被大量截取传播。" },
      { at: "剪辑成片", title: "条件句被移除", detail: "成片中保留了结论，删掉了前提，共剪去约 90 秒。" },
      { at: "传播 12 小时后", title: "完整对话稿出现", detail: "有读者整理出全文，讨论方向随之改变。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 完整对话稿片段",
      lines: [
        "「先说明一点，我说的情况只适用于当时那个阶段。」",
        "「如果换个时间点问我，答案可能不太一样。」",
      ],
    },
    openQuestions: [
      "成片时长的限制是否是删减的主要原因？",
      "完整对话稿目前是否还有更长的未公开版本？",
      "二次传播中的截取是否有统一的来源？",
    ],
    relatedSlugs: ["variety-cut-uncut-draft", "drama-screen-time-count"],
  },
  {
    id: 7,
    slug: "airport-photo-timeline",
    title: "一张行程照引发的猜测：从拍摄时间戳还原 26 小时的行踪线",
    excerpt:
      "同一张照片在三个平台被标注了三个不同的时间。把时间戳、发布的时区与航班信息放在一起，26 小时的行踪线就能被重新排列出来。",
    cover:
      "https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20an%20airport%20corridor%20with%20warm%20evening%20light%20through%20large%20windows%2C%20terracotta%20and%20amber%20palette%2C%20abstract%20geometric%20shapes%2C%20paper%20grain%20texture%2C%20no%20people&width=1200&height=750&seq=melon-cover-07&orientation=landscape",
    coverAlt: "傍晚机场廊桥的插画",
    topicSlug: "yule-shijian",
    topicName: "娱乐事件",
    date: "2026-06-26",
    readTime: "8 分钟",
    author: "林知",
    authorRole: "主编",
    heat: 78,
    status: "时间戳已交叉验证",
    tags: ["行程照", "时间戳", "时区"],
    highlights: [
      "三个平台显示的时间差异，主要来自发布时区不同，而不是三次拍摄。",
      "把航班起降时间代入后，26 小时的行踪线可以完整闭合。",
      "唯一无法确认的，是登机前的两小时空档。",
    ],
    sections: [
      {
        heading: "一、先解决时区问题",
        paragraphs: [
          "看起来最混乱的部分其实最容易解决。多数平台会按照账号所在地的时区显示发布时间，只要确认每个账号的默认时区，时间差就会消失。",
          "本次记录中，三个时间戳扣除时区差异后，全部落在同一天的两小时内。",
        ],
      },
      {
        heading: "二、用航班时间做交叉验证",
        paragraphs: [
          "航空信息是公开且带时间戳的。把已知的起降时间与照片中的环境特征对应后，可以确定拍摄发生在那趟航班落地之后，而不是起飞之前。",
          "剩下的两小时空档，恰好对应从到达层到上车点的通行时间，属于完全正常的范围。",
        ],
      },
      {
        heading: "三、不确定的部分要写清楚",
        paragraphs: [
          "把全部分钟都解释清楚，反而是不诚实的。本次还原中有一段约两小时的空档没有公开记录支撑，我们选择直接标注为待确认，而不是用推测补齐。",
          "对读者来说，知道哪里是空白，比得到一个看似完整的说法更重要。",
        ],
      },
    ],
    timeline: [
      { at: "第 1 天 18:40", title: "航班落地", detail: "公开的航班动态显示该班次于此时到达。" },
      { at: "第 1 天 19:05", title: "环境特征吻合", detail: "照片中的廊桥与舷窗光影与该航班抵达时间一致。" },
      { at: "第 1 天 19:12", title: "照片首次发布", detail: "首个发布账号的默认时区与当地时间一致。" },
      { at: "第 1 天 21:20", title: "约两小时空档", detail: "此区间没有公开记录，标注为待确认。" },
      { at: "第 2 天 00:30", title: "二次转发扩散", detail: "另两个平台的转发因时区不同显示为不同日期。" },
    ],
    rawExcerpt: {
      label: "未删减原稿 · 时间戳核对表",
      lines: [
        "「账号一（+08:00）：19:12 发布」",
        "「账号二（+09:00）：20:12 发布，同一张图」",
        "「差一小时是时区，不是第二次拍摄。」",
      ],
    },
    openQuestions: [
      "登机前的两小时是否有第三方记录可以补充？",
      "最初发布者的时区设置是否长期一致？",
      "二次转发中的时间标注由平台自动生成还是手动填写？",
    ],
    relatedSlugs: ["endorsement-termination-48hours", "interview-90-seconds"],
  },
];

export const featuredSlugs = [
  "music-show-song-swap-72hours",
  "drama-screen-time-count",
  "variety-cut-uncut-draft",
  "endorsement-termination-48hours",
];

export const topicSpotlight = [
  {
    slug: "zongyi-muhou",
    title: "录影棚里的顺序之谜",
    summary: "同一段对话被拆到三个位置，我们把顺剪稿的顺序重新排了一遍。",
    count: "18 篇",
  },
  {
    slug: "daiyan-fengbo",
    title: "物料的上下线时间差",
    summary: "官网、大屏、电商，三个渠道为什么总是不同步。",
    count: "12 篇",
  },
  {
    slug: "wangluo-redian",
    title: "被剪掉的九十秒",
    summary: "脱离语境的那句话，前面原本还有两句铺垫。",
    count: "24 篇",
  },
];